import { registerPlugin } from '@capacitor/core';

export interface UsbDeviceItem {
  deviceId: number;
  deviceName: string;
  vendorId: number;
  productId: number;
  vendorIdHex: string;
  productIdHex: string;
  productName: string;
  manufacturerName: string;
  serialNumber?: string;
  /** adb | serial | hub | storage | audio | hid | other */
  role?: string;
  isAdb?: boolean;
  className?: string;
  interfaceCount?: number;
  deviceProtocol?: number;
  usbVersionString?: string;
  hasPermission: boolean;
  isSupportedDriver: boolean;
  driverClass: string;
  portsCount?: number;
  isConnected: boolean;
}

export interface UsbDeviceStatusResult {
  connected: boolean;
  status: 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'ERROR';
  baudRate: number;
  deviceCount: number;
  activeDevice?: UsbDeviceItem | null;
  devices: UsbDeviceItem[];
  lastError?: string;
}

export interface UsbSendAndListenResult {
  success: boolean;
  command: string;
  response: string;
  durationMs: number;
  error?: string;
}

export interface UsbLogsResult {
  logs: string[];
  count: number;
}

interface UsbSerialPluginInterface {
  getDeviceStatus(): Promise<UsbDeviceStatusResult>;
  requestPermission(options?: { vendorId?: number; productId?: number }): Promise<{ granted: boolean; deviceName?: string }>;
  connect(options?: {
    vendorId?: number;
    productId?: number;
    baudRate?: number;
    dataBits?: number;
    stopBits?: number;
    parity?: number;
    /** Bind to a specific adapter (Android deviceId) and, on multi-port chips, a specific port. */
    deviceId?: number;
    portIndex?: number;
    /** Set false to avoid asserting DTR/RTS on open (asserting them resets many ESP32/Arduino boards). */
    dtr?: boolean;
    rts?: boolean;
    /** Resume the session automatically when the same adapter is re-plugged. */
    autoReconnect?: boolean;
  }): Promise<{ success: boolean; baudRate: number; status: string; error?: string }>;
  disconnect(): Promise<{ success: boolean; status: string }>;
  send(options: { data: string }): Promise<{ success: boolean; error?: string }>;
  sendHex(options: { hex: string }): Promise<{ success: boolean; bytesSent?: number; error?: string }>;
  sendDmx(options: { channels?: number[]; hex?: string }): Promise<{ success: boolean; packetLength?: number; error?: string }>;
  sendAndListen(options: { command: string; timeout?: number }): Promise<UsbSendAndListenResult>;
  getLogs(options?: { count?: number }): Promise<UsbLogsResult>;
  clearLogs(): Promise<{ success: boolean }>;
  addListener(eventName: string, listenerFunc: (data: any) => void): Promise<any>;
  removeAllListeners(): Promise<void>;
  setControlLine(options: { line: 'DTR' | 'RTS'; value: boolean }): Promise<{ success: boolean; error?: string }>;
  getControlLines(): Promise<UsbControlLines>;
  setBreak(options?: { value?: boolean }): Promise<{ success: boolean; error?: string }>;
  purge(options?: { rx?: boolean; tx?: boolean }): Promise<{ success: boolean; error?: string }>;
  setLineParameters(options: { baudRate?: number; dataBits?: number; stopBits?: number; parity?: number }): Promise<UsbLineParametersResult>;
  getStats(): Promise<UsbLinkStats>;
  describeDevice(options?: { deviceId?: number; vendorId?: number; productId?: number }): Promise<UsbDeviceDescriptor>;
  setAutoReconnect(options?: { enabled?: boolean }): Promise<{ success: boolean; autoReconnect: boolean }>;
}

export interface UsbControlLines {
  ok: boolean;
  error?: string;
  warning?: string;
  supported?: string[];
  active?: string[];
  levels?: Record<'RTS' | 'CTS' | 'DTR' | 'DSR' | 'CD' | 'RI', boolean>;
}

export interface UsbLineParametersResult {
  success: boolean;
  baudRate: number;
  dataBits: number;
  stopBits: number;
  parity: number;
  error?: string;
}

export interface UsbLinkStats {
  status: string;
  connected: boolean;
  txBytes: number;
  rxBytes: number;
  txFrames: number;
  rxFrames: number;
  errors: number;
  uptimeMs: number;
  msSinceLastRx: number;
  txBytesPerSec: number;
  rxBytesPerSec: number;
  baudRate: number;
  dataBits: number;
  stopBits: number;
  parity: number;
  portIndex: number;
  autoReconnect: boolean;
  logEntries: number;
  lastError: string;
}

export interface UsbDeviceDescriptor {
  ok: boolean;
  error?: string;
  note?: string;
  deviceId?: number;
  vendorIdHex?: string;
  productIdHex?: string;
  productName?: string;
  manufacturerName?: string;
  serialNumber?: string;
  hasPermission?: boolean;
  bcdUsb?: string;
  bcdDevice?: string;
  usbGeneration?: string;
  usbVersionString?: string;
  maxPacketSize0?: number;
  rawDescriptorsLength?: number;
  rawDescriptorsHex?: string;
  configurations?: Array<{ id: number; name: string; maxPowerMa: number; selfPowered: boolean; remoteWakeup: boolean; interfaceCount: number }>;
  interfaces?: Array<Record<string, any>>;
  [key: string]: any;
}

export const UsbSerialNative = registerPlugin<UsbSerialPluginInterface>('UsbSerial');

/**
 * Checks if the native UsbSerial plugin is running on Android
 */
export function isUsbSerialSupported(): boolean {
  return typeof (window as any).Capacitor !== 'undefined' && (window as any).Capacitor.isPluginAvailable('UsbSerial');
}

/**
 * Queries connected USB OTG devices and current serial state
 */
export async function getUsbDeviceStatus(): Promise<UsbDeviceStatusResult> {
  if (!isUsbSerialSupported()) {
    return {
      connected: false,
      status: 'DISCONNECTED',
      baudRate: 115200,
      deviceCount: 0,
      devices: [],
      lastError: 'Web preview: Native USB OTG requires Android device.'
    };
  }
  try {
    return await UsbSerialNative.getDeviceStatus();
  } catch (err: any) {
    return {
      connected: false,
      status: 'ERROR',
      baudRate: 115200,
      deviceCount: 0,
      devices: [],
      lastError: err.message
    };

  }
}

export { getUsbDeviceStatus as getUsbStatus };



/**
 * Requests USB permissions for an attached bridge
 */
export async function requestUsbPermission(vendorId?: number, productId?: number): Promise<boolean> {
  if (!isUsbSerialSupported()) return false;
  try {
    const res = await UsbSerialNative.requestPermission({ vendorId, productId });
    return Boolean(res.granted);
  } catch (err) {
    console.warn('[UsbSerial] Failed to request permission:', err);
    return false;
  }
}

/**
 * Connects to a USB Serial device at specified baud rate (default: 115200 8N1)
 */
export async function connectUsbSerial(
  baudRate: number = 115200,
  vendorId?: number,
  productId?: number
): Promise<{ success: boolean; error?: string }> {
  if (!isUsbSerialSupported()) {
    return { success: false, error: 'UsbSerial plugin unavailable in current runtime.' };
  }
  try {
    const res = await UsbSerialNative.connect({
      baudRate,
      vendorId,
      productId,
      dataBits: 8,
      stopBits: 1,
      parity: 0
    });
    return { success: res.success, error: res.error };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Disconnects active serial connection
 */
export async function disconnectUsbSerial(): Promise<boolean> {
  if (!isUsbSerialSupported()) return true;
  try {
    const res = await UsbSerialNative.disconnect();
    return Boolean(res.success);
  } catch (err) {
    console.warn('[UsbSerial] Disconnect error:', err);
    return false;
  }
}

/**
 * Transmits string/command over serial
 */
export async function sendUsbCommand(data: string): Promise<{ success: boolean; error?: string }> {
  if (!isUsbSerialSupported()) {
    return { success: false, error: 'USB Serial not available.' };
  }
  try {
    const res = await UsbSerialNative.send({ data });
    return { success: res.success, error: res.error };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Sends a raw hexadecimal payload over USB Serial (binary safe)
 */
export async function sendUsbHex(hex: string): Promise<{ success: boolean; bytesSent?: number; error?: string }> {
  if (!isUsbSerialSupported()) {
    return { success: false, error: 'USB Serial not available.' };
  }
  try {
    const res = await UsbSerialNative.sendHex({ hex });
    return { success: res.success, bytesSent: res.bytesSent, error: res.error };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Sends a framed Enttec DMX USB Pro packet directly to the transceiver
 */
export async function sendUsbDmx(
  channels: number[] | Uint8Array | string
): Promise<{ success: boolean; packetLength?: number; error?: string }> {
  if (!isUsbSerialSupported()) {
    return { success: false, error: 'USB Serial not available.' };
  }
  try {
    let payload: { channels?: number[]; hex?: string } = {};
    if (typeof channels === 'string') {
      payload.hex = channels;
    } else if (channels instanceof Uint8Array) {
      payload.channels = Array.from(channels);
    } else {
      payload.channels = channels;
    }
    const res = await UsbSerialNative.sendDmx(payload);
    return { success: res.success, packetLength: res.packetLength, error: res.error };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Sends a command and collects incoming response lines within timeout window
 */
export async function sendAndListenUsbSerial(
  command: string,
  timeoutMs: number = 2000
): Promise<UsbSendAndListenResult> {
  if (!isUsbSerialSupported()) {
    return {
      success: false,
      command,
      response: '',
      durationMs: 0,
      error: 'USB OTG rig only accessible when running on Android hardware.'
    };
  }
  try {
    return await UsbSerialNative.sendAndListen({ command, timeout: timeoutMs });
  } catch (err: any) {
    return {
      success: false,
      command,
      response: '',
      durationMs: 0,
      error: err.message
    };
  }
}

/**
 * Retrieves the recent serial log buffer
 */
export async function getUsbLogs(count: number = 100): Promise<string[]> {
  if (!isUsbSerialSupported()) return [];
  try {
    const res = await UsbSerialNative.getLogs({ count });
    return res.logs || [];
  } catch {
    return [];
  }
}

/**
 * Clears the circular ring buffer
 */
export async function clearUsbLogs(): Promise<boolean> {
  if (!isUsbSerialSupported()) return true;
  try {
    const res = await UsbSerialNative.clearLogs();
    return Boolean(res.success);
  } catch {
    return false;
  }
}


/**
 * Connects with full control over line settings, adapter selection and reset behaviour.
 */
export async function connectUsbSerialAdvanced(options: {
  baudRate?: number;
  dataBits?: number;
  stopBits?: number;
  parity?: number;
  vendorId?: number;
  productId?: number;
  deviceId?: number;
  portIndex?: number;
  dtr?: boolean;
  rts?: boolean;
  autoReconnect?: boolean;
}): Promise<{ success: boolean; error?: string }> {
  if (!isUsbSerialSupported()) {
    return { success: false, error: 'UsbSerial plugin unavailable in current runtime.' };
  }
  try {
    const res = await UsbSerialNative.connect({ baudRate: 115200, dataBits: 8, stopBits: 1, parity: 0, ...options });
    return { success: res.success, error: res.error };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/** Drive DTR or RTS (e.g. pulse RTS/DTR to reset or enter the bootloader on ESP32 / Arduino boards). */
export async function setUsbControlLine(line: 'DTR' | 'RTS', value: boolean): Promise<{ success: boolean; error?: string }> {
  if (!isUsbSerialSupported()) return { success: false, error: 'USB Serial not available.' };
  try {
    return await UsbSerialNative.setControlLine({ line, value });
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/** Reads which modem-control lines the adapter supports and which are currently asserted. */
export async function getUsbControlLines(): Promise<UsbControlLines> {
  if (!isUsbSerialSupported()) return { ok: false, error: 'USB Serial not available.' };
  try {
    return await UsbSerialNative.getControlLines();
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

/** Changes baud rate / framing on the open port without reconnecting. */
export async function setUsbLineParameters(params: {
  baudRate?: number;
  dataBits?: number;
  stopBits?: number;
  parity?: number;
}): Promise<UsbLineParametersResult | { success: false; error: string }> {
  if (!isUsbSerialSupported()) return { success: false, error: 'USB Serial not available.' };
  try {
    return await UsbSerialNative.setLineParameters(params);
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/** Sends or clears a BREAK condition. */
export async function setUsbBreak(value: boolean = true): Promise<{ success: boolean; error?: string }> {
  if (!isUsbSerialSupported()) return { success: false, error: 'USB Serial not available.' };
  try {
    return await UsbSerialNative.setBreak({ value });
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/** Flushes the adapter's hardware RX/TX FIFOs. */
export async function purgeUsbBuffers(rx: boolean = true, tx: boolean = true): Promise<{ success: boolean; error?: string }> {
  if (!isUsbSerialSupported()) return { success: false, error: 'USB Serial not available.' };
  try {
    return await UsbSerialNative.purge({ rx, tx });
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/** Live link statistics: byte/frame counters, throughput, errors, uptime. */
export async function getUsbLinkStats(): Promise<UsbLinkStats | null> {
  if (!isUsbSerialSupported()) return null;
  try {
    return await UsbSerialNative.getStats();
  } catch {
    return null;
  }
}

/** Full descriptor dump for a device (interfaces, endpoints, configurations, raw descriptor hex). */
export async function describeUsbDevice(selector: { deviceId?: number; vendorId?: number; productId?: number } = {}): Promise<UsbDeviceDescriptor> {
  if (!isUsbSerialSupported()) return { ok: false, error: 'USB Serial not available.' };
  try {
    return await UsbSerialNative.describeDevice(selector);
  } catch (err: any) {
    return { ok: false, error: err.message };
  }
}

/** Enables/disables resuming the session when the same adapter is re-plugged. */
export async function setUsbAutoReconnect(enabled: boolean): Promise<boolean> {
  if (!isUsbSerialSupported()) return false;
  try {
    const res = await UsbSerialNative.setAutoReconnect({ enabled });
    return Boolean(res.success);
  } catch {
    return false;
  }
}